import React, { useState, useEffect, useMemo } from 'react';
import { baseUrl } from '../components/BaseUrl';
import { Link } from 'react-router-dom';
import { MyProSidebarProvider } from "../components/SidebarContext";
import TopBar  from '../components/Topbar';
import Footer  from '../components/Footer';
import '../App.css';
import axios from 'axios';
import Swal from "sweetalert2";
import GreetUser  from '../components/GreetUser';
import AOS from 'aos';
import 'aos/dist/aos.css';
AOS.init();

const Quiz = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [questionsFilter, setQuestionsFilter] = useState('all');

  useEffect(() => {
    fetchQuiz();
  }, []);

  const fetchQuiz = async () => {
    // const token = localStorage.getItem("codefussion-login-token");
    setLoading(true);
    axios.get(`${baseUrl}get_quizzes.php`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // Authorization: token
      }
    })
    .then(response => {
      console.log(response)
      // Handle the API response structure
      if (response.data.status === 'success') {
        setData(response.data.data || []);
      } else {
        setData([]);
      }
      setLoading(false);
    })
    .catch((error) => {
      Swal.fire({
        title: "Error",
        text: "An error occurred. Please try again later",
        icon: "error",
        confirmButtonText: "OK",
        backdrop: `rgba(143, 70, 0, 0.3) left top no-repeat`
      });
      setLoading(false);
    });
  }

  // Get unique difficulties and create question count ranges
  const difficulties = useMemo(() => {
    const uniqueDifficulties = [...new Set(data.map(item => item.difficulty))].filter(Boolean);
    return uniqueDifficulties.sort();
  }, [data]);

  // Real-time filtering
  const filteredQuizzes = useMemo(() => {
    let filtered = data;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(quiz => 
        quiz.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quiz.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Difficulty filter
    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(quiz => quiz.difficulty === difficultyFilter);
    }

    // Questions count filter
    if (questionsFilter !== 'all') {
      const [min, max] = questionsFilter.split('-').map(Number);
      filtered = filtered.filter(quiz => {
        const questions = quiz.questions_count || 0;
        if (max) {
          return questions >= min && questions <= max;
        } else {
          return questions >= min;
        }
      });
    }

    // Duration filter
    if (durationFilter !== 'all') {
      filtered = filtered.filter(quiz => {
        const duration = quiz.duration?.toLowerCase() || '';
        if (durationFilter === 'short') {
          return duration.includes('min') && !duration.includes('hour');
        } else if (durationFilter === 'medium') {
          return duration.includes('30') || duration.includes('45') || duration.includes('60');
        } else if (durationFilter === 'long') {
          return duration.includes('hour') || parseInt(duration) > 60;
        }
        return true;
      });
    }

    return filtered;
  }, [data, searchTerm, difficultyFilter, questionsFilter, durationFilter]);

  // Get counts for filters
  const getQuizCounts = () => {
    const easy = data.filter(quiz => quiz.difficulty.toLowerCase() === 'easy').length;
    const medium = data.filter(quiz => quiz.difficulty.toLowerCase() === 'medium').length;
    const hard = data.filter(quiz => quiz.difficulty.toLowerCase() === 'hard').length;
    
    return { total: data.length, easy, medium, hard };
  }

  const quizCounts = getQuizCounts();

  const showQuiz = () => {
    try {
      if (filteredQuizzes.length === 0) {
        return (
          <div className="col-12 text-center py-5">
            <div className="no-quizzes">
              <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
              <h4>No quizzes found</h4>
              <p className="text-muted">
                {searchTerm 
                  ? `No quizzes match "${searchTerm}". Try a different search term.`
                  : "No quizzes match your selected filters. Try changing your filter options."}
              </p>
              {(searchTerm || difficultyFilter !== 'all' || questionsFilter !== 'all' || durationFilter !== 'all') && (
                <button 
                  className="btn btn-primary mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setDifficultyFilter('all');
                    setQuestionsFilter('all');
                    setDurationFilter('all');
                  }}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        );
      }

      return filteredQuizzes.map((item, index) => {
        const shortenString = item.description?.length > 36 
          ? `${item.description.substring(0, 45)}...`
          : item.description || 'Test your knowledge with this engaging quiz.';

        const questionsCount = item.questions_count || 0;
        const duration = item.duration || 'Not specified';

        return (
          <div 
            key={item.id || index}
            data-aos="fade-up" 
            data-aos-duration="1000"
            className="col-lg-4 col-md-6 p-3 col-sm-12 item"
          >
            <div className="quiz-card card card-body mb-4 h-100">
              {/* Difficulty Badge */}
              <div className={`difficulty-badge ${item.difficulty?.toLowerCase() || 'medium'}`}>
                {item.difficulty || 'Medium'}
              </div>

              <img 
                src={item.image} 
                alt={item.title} 
                className="quiz-image img-fluid rounded"
              />
              
              <div className="quiz-content">
                <h3 className="quiz-title">{item.title}</h3>
                <p className="quiz-description">{shortenString}</p>
                
                <div className="quiz-meta">
                  <div className="meta-item">
                    <i className="fas fa-question-circle"></i>
                    <span>{questionsCount} questions</span>
                  </div>
                  <div className="meta-item">
                    <i className="fas fa-clock"></i>
                    <span>{duration}</span>
                  </div>
                  <div className="meta-item">
                    <i className="fas fa-users"></i>
                    <span>{Math.floor(Math.random() * 1000) + 100} taken</span>
                  </div>
                </div>

                <div className="quiz-stats">
                  <div className="stat">
                    <div className="stat-label">Success Rate</div>
                    <div className="stat-value">{Math.floor(Math.random() * 30) + 60}%</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Avg. Score</div>
                    <div className="stat-value">{Math.floor(Math.random() * 30) + 60}%</div>
                  </div>
                </div>

                <Link to={`/quiz-description/${item.id}`}>
                  <button className="btn btn-primary text-light w-100 quiz-btn" type="button">
                    <i className="fas fa-play-circle me-2"></i>
                    Start Assessment
                  </button>
                </Link>
              </div>
            </div>
          </div>
        );
      });
    } catch (e) {
      console.error('Error loading quizzes:', e);
      return (
        <div className="col-12">
          <p className="text-danger">
            An error occurred while loading quizzes. Please try again later.
          </p>
        </div>
      );
    }
  };

  return (
    <MyProSidebarProvider>
      {localStorage.getItem("codefussion-login-token") === null ? window.location.replace("/login") :
      <div>
        <TopBar />
        <div className="container">
          <div className="col-md-12">
            {loading ? (
              <div style={{ margin: 'auto', width: '50%', padding: '50px' }}>
                <div className="loader-wrapper" id="loading">
                  <div className="loader">
                    <div className="loader loader-inner"></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="quizzes-container">
                <h2 className="text-dark mt-4 font-weight-bold">Start Your Assessment</h2>
                {!(localStorage.getItem("codefussion-subscription-status") === "active") && 
                  <p className='text-center text-warning'> 
                    You do not have an active subscription, but you can try the HTML quiz for free
                  </p>
                }

                {/* Search Bar */}
                <div className="search-container mb-4">
                  <div className="search-box">
                    <i className="fas fa-search search-icon"></i>
                    <input
                      type="text"
                      placeholder="Search quizzes by title or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    {searchTerm && (
                      <button 
                        className="clear-search"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter Section */}
                <div className="filter-section mb-4">
                  <div className="filter-tabs">
                    <button 
                      className={`filter-btn ${difficultyFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setDifficultyFilter('all')}
                    >
                      All Quizzes
                      <span className="count-badge">{quizCounts.total}</span>
                    </button>
                    <button 
                      className={`filter-btn ${difficultyFilter === 'Easy' ? 'active' : ''}`}
                      onClick={() => setDifficultyFilter('Easy')}
                    >
                      Easy
                      <span className="count-badge easy-count">{quizCounts.easy}</span>
                    </button>
                    <button 
                      className={`filter-btn ${difficultyFilter === 'Medium' ? 'active' : ''}`}
                      onClick={() => setDifficultyFilter('Medium')}
                    >
                      Medium
                      <span className="count-badge medium-count">{quizCounts.medium}</span>
                    </button>
                    <button 
                      className={`filter-btn ${difficultyFilter === 'Hard' ? 'active' : ''}`}
                      onClick={() => setDifficultyFilter('Hard')}
                    >
                      Hard
                      <span className="count-badge hard-count">{quizCounts.hard}</span>
                    </button>
                  </div>

                  {/* Additional Filters */}
                  <div className="dropdown-filters">
                    <div className="filter-dropdown">
                      <label>Questions:</label>
                      <select 
                        value={questionsFilter} 
                        onChange={(e) => setQuestionsFilter(e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">Any Number</option>
                        <option value="1-10">1-10 Questions</option>
                        <option value="11-20">11-20 Questions</option>
                        <option value="21-30">21-30 Questions</option>
                        <option value="31-50">31+ Questions</option>
                      </select>
                    </div>

                    <div className="filter-dropdown">
                      <label>Duration:</label>
                      <select 
                        value={durationFilter} 
                        onChange={(e) => setDurationFilter(e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">Any Duration</option>
                        <option value="short">Short (Under 30 min)</option>
                        <option value="medium">Medium (30-60 min)</option>
                        <option value="long">Long (1+ hours)</option>
                      </select>
                    </div>
                  </div>

                  {/* Active Filters Display */}
                  {(searchTerm || difficultyFilter !== 'all' || questionsFilter !== 'all' || durationFilter !== 'all') && (
                    <div className="active-filters">
                      <span className="active-filters-label">Active filters:</span>
                      {searchTerm && (
                        <span className="filter-chip">
                          Search: "{searchTerm}"
                          <button onClick={() => setSearchTerm('')}>×</button>
                        </span>
                      )}
                      {difficultyFilter !== 'all' && (
                        <span className="filter-chip">
                          Difficulty: {difficultyFilter}
                          <button onClick={() => setDifficultyFilter('all')}>×</button>
                        </span>
                      )}
                      {questionsFilter !== 'all' && (
                        <span className="filter-chip">
                          Questions: {questionsFilter.replace('-', ' to ')}
                          <button onClick={() => setQuestionsFilter('all')}>×</button>
                        </span>
                      )}
                      {durationFilter !== 'all' && (
                        <span className="filter-chip">
                          Duration: {durationFilter}
                          <button onClick={() => setDurationFilter('all')}>×</button>
                        </span>
                      )}
                      <button 
                        className="clear-all-filters"
                        onClick={() => {
                          setSearchTerm('');
                          setDifficultyFilter('all');
                          setQuestionsFilter('all');
                          setDurationFilter('all');
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                  )}
                </div>

                {/* Results Count */}
                <div className="results-count mb-3">
                  <strong>{filteredQuizzes.length}</strong> 
                  {filteredQuizzes.length === 1 ? ' quiz' : ' quizzes'} 
                  {searchTerm && ` matching "${searchTerm}"`}
                </div>

                <div className="row row-cols-1 row-cols-md-2">
                  {showQuiz()}
                </div>
              </div>
            )}
          </div>
        </div>

        <style jsx>{`
          .quizzes-container {
            padding: 0 1rem;
          }

          /* Search Bar Styles */
          .search-container {
            max-width: 600px;
          }

          .search-box {
            position: relative;
            display: flex;
            align-items: center;
          }

          .search-icon {
            position: absolute;
            left: 15px;
            color: #6c757d;
            z-index: 2;
          }

          .search-input {
            width: 100%;
            padding: 12px 45px 12px 45px;
            border: 2px solid #e0e0e0;
            border-radius: 25px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: white;
          }

          .search-input:focus {
            outline: none;
            border-color: #0066ff;
            box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
          }

          .clear-search {
            position: absolute;
            right: 15px;
            background: none;
            border: none;
            color: #6c757d;
            cursor: pointer;
            padding: 5px;
            border-radius: 50%;
            transition: all 0.2s ease;
          }

          .clear-search:hover {
            background: #f8f9fa;
            color: #495057;
          }

          /* Filter Section */
          .filter-section {
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 1rem;
          }

          .filter-tabs {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            margin-bottom: 1rem;
          }

          .filter-btn {
            position: relative;
            padding: 10px 20px;
            border: 2px solid #e0e0e0;
            background: white;
            color: #495057;
            border-radius: 25px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .filter-btn:hover {
            border-color: #0066ff;
            color: #0066ff;
            transform: translateY(-2px);
          }

          .filter-btn.active {
            background: #0066ff;
            border-color: #0066ff;
            color: white;
          }

          .count-badge {
            background: rgba(0, 0, 0, 0.1);
            color: inherit;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: 600;
          }

          .filter-btn.active .count-badge {
            background: rgba(255, 255, 255, 0.3);
          }

          .easy-count {
            background: rgba(0, 204, 136, 0.2);
            color: #008055;
          }

          .medium-count {
            background: rgba(255, 170, 0, 0.2);
            color: #b8860b;
          }

          .hard-count {
            background: rgba(255, 68, 68, 0.2);
            color: #cc4444;
          }

          /* Dropdown Filters */
          .dropdown-filters {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
            margin-bottom: 1rem;
          }

          .filter-dropdown {
            display: flex;
            flex-direction: column;
            gap: 5px;
          }

          .filter-dropdown label {
            font-weight: 600;
            color: #495057;
            font-size: 0.9rem;
          }

          .filter-select {
            padding: 8px 12px;
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            background: white;
            cursor: pointer;
            transition: border-color 0.3s ease;
          }

          .filter-select:focus {
            outline: none;
            border-color: #0066ff;
          }

          /* Active Filters */
          .active-filters {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .active-filters-label {
            font-weight: 600;
            color: #495057;
          }

          .filter-chip {
            display: flex;
            align-items: center;
            gap: 5px;
            background: #e9ecef;
            padding: 5px 12px;
            border-radius: 20px;
            font-size: 0.9rem;
          }

          .filter-chip button {
            background: none;
            border: none;
            cursor: pointer;
            padding: 2px;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s ease;
          }

          .filter-chip button:hover {
            background: #dee2e6;
          }

          .clear-all-filters {
            background: none;
            border: none;
            color: #0066ff;
            cursor: pointer;
            font-weight: 600;
            padding: 5px 10px;
            border-radius: 4px;
            transition: background 0.2s ease;
          }

          .clear-all-filters:hover {
            background: #f8f9fa;
          }

          /* Results Count */
          .results-count {
            color: #6c757d;
            font-size: 0.9rem;
          }

          .results-count strong {
            color: #0066ff;
          }

          /* Quiz Card Styles */
          .quiz-card {
            position: relative;
            overflow: hidden;
            transition: all 0.3s ease;
            border: 1px solid #e0e0e0;
            border-radius: 12px;
          }

          .quiz-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 8px 30px rgba(0,0,0,0.12);
            border-color: #0066ff;
          }

          .quiz-image {
            height: 120px;
            object-fit: cover;
            width: 100%;
            border-radius: 8px 8px 0 0;
          }

          .quiz-content {
            padding: 1rem;
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .quiz-title {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: #1a1a1a;
          }

          .quiz-description {
            color: #666;
            margin-bottom: 1rem;
            flex: 1;
          }

          /* Difficulty Badge */
          .difficulty-badge {
            position: absolute;
            top: 12px;
            right: 12px;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            z-index: 2;
          }

          .difficulty-badge.easy {
            background: rgba(0, 204, 136, 0.2);
            color: #008055;
            border: 1px solid #00cc88;
          }

          .difficulty-badge.medium {
            background: rgba(255, 170, 0, 0.2);
            color: #b8860b;
            border: 1px solid #ffaa00;
          }

          .difficulty-badge.hard {
            background: rgba(255, 68, 68, 0.2);
            color: #cc4444;
            border: 1px solid #ff4444;
          }

          /* Quiz Meta */
          .quiz-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 1rem;
            flex-wrap: wrap;
            gap: 8px;
          }

          .meta-item {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 0.85rem;
            color: #666;
          }

          .meta-item i {
            color: #0066ff;
          }

          /* Quiz Stats */
          .quiz-stats {
            display: flex;
            justify-content: space-around;
            background: #f8f9fa;
            padding: 0.75rem;
            border-radius: 8px;
            margin-bottom: 1rem;
          }

          .stat {
            text-align: center;
          }

          .stat-label {
            font-size: 0.75rem;
            color: #666;
            margin-bottom: 2px;
          }

          .stat-value {
            font-size: 1rem;
            font-weight: 700;
            color: #0066ff;
          }

          /* Quiz Button */
          .quiz-btn {
            background: #0066ff;
            border: none;
            padding: 12px 20px;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s ease;
          }

          .quiz-btn:hover {
            background: #0052cc;
            transform: translateY(-2px);
          }

          /* No Quizzes State */
          .no-quizzes {
            padding: 3rem 1rem;
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .filter-tabs {
              gap: 5px;
            }
            
            .filter-btn {
              padding: 8px 16px;
              font-size: 0.9rem;
            }
            
            .search-input {
              padding: 10px 45px 10px 45px;
            }

            .dropdown-filters {
              gap: 10px;
            }

            .filter-dropdown {
              flex: 1;
              min-width: 150px;
            }

            .quiz-meta {
              flex-direction: column;
              align-items: flex-start;
            }
          }

          @media (max-width: 576px) {
            .dropdown-filters {
              flex-direction: column;
            }

            .filter-dropdown {
              width: 100%;
            }
          }
        `}</style>
      </div>
      }
      <a href="https://api.whatsapp.com/send?phone=+2347089386320&text=Hello" className="float" target="_blank">
        <i className="fab fa-whatsapp my-float"></i>
      </a>
    </MyProSidebarProvider>
  );
}

export default Quiz;